import { useEffect, useState } from "react";
import { useBnbSelector } from "../../../redux/store";
import type {
    AccentColourType,
    ExtraSetItemType,
    ExtraType,
    PriceType,
    UpholsteryType,
    WheelType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { find } from "../../../../../../common-deprecated/utils";
import Debug from "../../../../../../common-deprecated/Debug";
import { getExtraInformation } from "../../../../../shared-logic/utils/packUtils";
import { useBnbOptionalLabel } from "../../../utils/labels";
import { getPriceFromExtra } from "../../../../../shared-logic/utils/priceUtils";

export type useExtraReturnType = {
    showAccessoryDisclaimer: boolean;
    renderedOptions: ExtraType[];
    renderedPrice: PriceType;
    includedItems?: ExtraType[];
    renderedSelectedOptionId: string;
    renderedOptionSelect?: (optionId: string) => void;
    setItems?: ExtraSetItemType[];
    renderedSelect?: (optionId?: string) => void;
    renderedImage: string;
};

const useExtra = (
    extra: ExtraType | WheelType | UpholsteryType,
    equipped: boolean,
    equippedOptionId: string,
    secondaryAccentColours?: AccentColourType[],
    onSelect?: (optionId?: string) => void,
    onOptionSelect?: (optionId: string) => void,
): useExtraReturnType => {
    const [selectedOptionId, setSelectedOptionId] = useState<string>(
        extra.objectType === "extra" ? extra.selectedOptionId : "",
    );
    const basket = useBnbSelector((state) => state.basket);
    const items = useBnbSelector((state) => state.items);
    const accessoryDisclaimerIsDefined = !!useBnbOptionalLabel("accessoryDisclaimer");

    const extraInformation = getExtraInformation(extra, basket, items, accessoryDisclaimerIsDefined);
    // Child options can impact what is actually rendered, so start with defaults and change them if required.
    let { renderedPrice, renderedImage } = extraInformation;
    const { showAccessoryDisclaimer, setItems, includedItems } = extraInformation;

    let renderedOptions: ExtraType[] = [];
    let renderedSelectedOptionId = "";
    let renderedOptionSelect = onOptionSelect;
    let renderedSelect = onSelect;

    // Update selectedOptionId state to make sure the visualized childOption doesn't change when the extra is deselected.
    useEffect(() => {
        if (equippedOptionId && equippedOptionId !== selectedOptionId) {
            setSelectedOptionId(equippedOptionId);
        }
    }, [equippedOptionId, selectedOptionId]);

    // Child option logic.
    if (extra.objectType === "extra") {
        renderedPrice = getPriceFromExtra(extra, equippedOptionId);

        if (extra.options.length) {
            // Filter the options to only show the options which are allowed to be rendered.
            renderedOptions = extra.options.filter(
                (option) => CarDataConsolidator.isAvailable(option, basket.colour, basket.upholstery).available,
            );
            // Additional filtering for secondary accent colours with colour dependencies.
            // TODO This line doesn't seem right, it does not even take colouringMode of the childOption into account
            if (secondaryAccentColours) {
                renderedOptions = renderedOptions.filter(
                    (option) => option.colour && find(option.colour.id, secondaryAccentColours),
                );
            }

            const userSelectedOptionId = equippedOptionId || selectedOptionId;
            const userSelectedOption = userSelectedOptionId ? find(userSelectedOptionId, renderedOptions) : false;

            // If no option has been selected by the user, render the first one available as selected.
            const activeOption = userSelectedOption || renderedOptions[0];

            if (activeOption) {
                renderedSelectedOptionId = activeOption.id;
                renderedImage = activeOption.image;

                // See OR-1144.
                // This will make sure the rendered childOptions can be selected without being added to the basket (so only the image changes).
                renderedOptionSelect = (optionId: string) => {
                    if (!equipped) setSelectedOptionId(optionId);
                    else if (onOptionSelect) onOptionSelect(optionId);
                };

                // This will make sure the correct childOption is added to the basket when the extra is selected.
                // BasketLogic will make sure the parent extra is selected so we can always use onOptionSelect.
                renderedSelect = () => onSelect && onSelect(activeOption.id);
            } else {
                // There should always be an active childOption. If that is not the case there is a wrong (availability) configuration.
                // Log an error if this is the case, the rest of the app should keep working though.
                Debug.error(`No active child option found for ${extra.name}. Configuration error?`);
            }
        }
    }

    return {
        renderedPrice,
        renderedOptions,
        renderedSelectedOptionId,
        renderedImage,
        renderedOptionSelect,
        renderedSelect,
        showAccessoryDisclaimer,
        setItems,
        includedItems,
    };
};

export default useExtra;
